'use strict';

import Swiper from 'swiper';
import { FreeMode, Mousewheel, Navigation, Pagination, Keyboard } from 'swiper/modules';

const SELECTOR = '.js-gallery';

export function init() {
    const elements = document.querySelectorAll(SELECTOR);

    if (!elements.length) {
        return;
    }

    elements.forEach(element => {
        const slidesContainerElement = element.querySelector('.image-gallery__content');
        const slideDetailSelector = element.dataset.gallerySlideDetailSelector || '.gallery-slide__content';

        const fixedBodyClass = element.dataset.galleryFixedBodyClass || 'image-gallery-fixed-body';
        const fullscreenClass = element.dataset.galleryFullscreenClass || 'image-gallery--fullscreen';

        const detailsContainer = element.querySelector(
            element.dataset.galleryDetailsContainerSelector || '.image-gallery__details'
        );

        const positionContainer = element.querySelector(
            element.dataset.galleryPositionContainerSelector || '.image-gallery__position'
        );

        const prevButtons = Array.from(element.querySelectorAll(
            element.dataset.galleryPrevButtonSelector || '.image-gallery__button--previous'
        ));

        const nextButtons = Array.from(element.querySelectorAll(
            element.dataset.galleryNextButtonSelector || '.image-gallery__button--next'
        ));

        const fullscreenButton = element.querySelector(
            element.dataset.galleryFullscreenButtonSelector || '.image-gallery__button--fullscreen'
        );

        const images = element.querySelectorAll(
            element.dataset.galleryImageSelector || '.gallery-slide__image'
        );

        const indexContainer = element.querySelector('.image-gallery__index');
        const totalSlides = element.querySelectorAll('.image-gallery__slide').length;
        const isSingleSlide = totalSlides === 1;

        // Caption source-of-truth: an array of detail elements indexed by
        // slide position, so captions are read from the same index that
        // already drives the slide counter -- rather than querying for
        // whichever slide currently has the active class, which is
        // unreliable in Swiper's freeMode.
        const slideDetailElements = Array.from(element.querySelectorAll(slideDetailSelector));

        if (isSingleSlide) {
            prevButtons.forEach(btn => btn.classList.add('disabled'));
            nextButtons.forEach(btn => btn.classList.add('disabled'));
        }

        // --- Visibility tracking setup (must exist before Swiper is
        // created/initialised below, since swiper.init() synchronously
        // fires the 'init' event, which calls updateMeta() -> getCurrentIndex()
        // immediately -- if currentVisibleIndex/getCurrentIndex aren't
        // ready by then, this throws instead of defaulting to 0). ---

        let currentVisibleIndex = 0;
        let intersectionObserverActive = false;

        function buildThresholdList(steps) {
            const thresholds = [];
            for (let i = 0; i <= steps; i++) {
                thresholds.push(i / steps);
            }
            return thresholds;
        }

        function getCurrentIndex() {
            if (intersectionObserverActive) {
                return currentVisibleIndex;
            }
            return typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;
        }

        function updateMeta() {
            if (!detailsContainer) {
                return;
            }

            const current = getCurrentIndex();
            const slideDetailContainer = slideDetailElements[current];

            // Visible, Console-free way to verify this code is running and
            // what index it thinks is active -- inspect this element in
            // DevTools Elements tab and compare against the image on screen.
            detailsContainer.setAttribute('data-debug-index', current);

            if (!slideDetailContainer) {
                return;
            }

            detailsContainer.innerHTML = slideDetailContainer.innerHTML;
        }

        function updateNavigationButtons() {
            prevButtons.forEach(btn => btn.classList.toggle('disabled', swiper.isBeginning));
            nextButtons.forEach(btn => btn.classList.toggle('disabled', swiper.isEnd));
        }

        function updateIndex() {
            if (!indexContainer) return;
            const current = getCurrentIndex() + 1;
            indexContainer.textContent = `${current} / ${totalSlides}`;
            indexContainer.setAttribute('aria-label', `Image ${current} of ${totalSlides}`);
        }

        function isGalleryFullscreen() {
            return element.classList.contains(fullscreenClass);
        }

        let documentScrollTop = 0;

        function trapFocus(event) {
            const focusableElements = Array.from(element.querySelectorAll('button, a, input, [tabindex="0"]')).filter(el => !el.classList.contains('disabled'));
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            if (event.key === 'Tab') {
                if (event.shiftKey) {
                    if (document.activeElement === firstFocusable) {
                        lastFocusable.focus();
                        event.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastFocusable) {
                        firstFocusable.focus();
                        event.preventDefault();
                    }
                }
            }
        }

        function startFullscreen() {
            switchImages();
            element.classList.add(fullscreenClass);
            documentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            document.body.classList.add(fixedBodyClass);
            document.querySelector('.image-gallery__fullscreen-close').focus();
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', trapFocus);
        }

        function endFullscreen() {
            switchImages();
            element.classList.remove(fullscreenClass);
            document.body.classList.remove(fixedBodyClass);
            document.documentElement.scrollTop = document.body.scrollTop = documentScrollTop;
            document.body.style.overflow = '';
            document.removeEventListener('keydown', trapFocus);
        }

        function switchImages() {
            images.forEach(image => {
                const { fullscreenSrc } = image.dataset;

                if (!fullscreenSrc) {
                    return;
                }

                image.dataset.fullscreenSrc = image.src;
                image.src = fullscreenSrc;
            });
        }

        // Track which slide is most visible using IntersectionObserver,
        // rather than Swiper's own realIndex/activeIndex or manually
        // computed geometry. This directly measures on-screen visibility
        // and updates continuously regardless of *how* the scroll happens
        // (touch, wheel, momentum, snap) -- so it can't skip a slide's
        // "current" window the way event-driven detection could.
        //
        // Falls back to Swiper's own realIndex/activeIndex in browsers
        // without IntersectionObserver support (e.g. Opera Mini, KaiOS 2.5) --
        // less precise during freeMode scroll, but avoids breaking entirely.
        if (typeof IntersectionObserver === 'function') {
            const visibilityRatios = new Map();
            const slideElementsForObserver = Array.from(element.querySelectorAll('.image-gallery__slide'));

            const intersectionObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    const idx = slideElementsForObserver.indexOf(entry.target);
                    if (idx === -1) return;
                    visibilityRatios.set(idx, entry.intersectionRatio);
                });

                let bestIndex = 0;
                let bestRatio = -1;
                visibilityRatios.forEach((ratio, idx) => {
                    if (ratio > bestRatio) {
                        bestRatio = ratio;
                        bestIndex = idx;
                    }
                });

                if (bestIndex !== currentVisibleIndex) {
                    currentVisibleIndex = bestIndex;
                    updateMeta();
                    updateIndex();
                }
            }, {
                // slidesContainerElement is already a resolved DOM element --
                // NOT a selector string -- so it's used directly here, not
                // passed through querySelector() again.
                root: slidesContainerElement || null,
                threshold: buildThresholdList(20),
            });

            slideElementsForObserver.forEach(slideEl => intersectionObserver.observe(slideEl));
            intersectionObserverActive = true;
        }

        // --- Swiper setup ---

        let swiper = new Swiper(slidesContainerElement, {
            init: false,
            modules: [
                FreeMode,
                Mousewheel,
                Navigation,
                Pagination,
                Keyboard
            ],
            slidesPerView: 'auto',
            simulateTouch: false,
            freeMode: {
                enabled: true,
                sticky: true,
            },
            mousewheel: {
                releaseOnEdges: true,
                forceToAxis: true,
                sensitivity: .8,
            },
            navigation: {
                prevEl: prevButtons,
                nextEl: nextButtons,
            },
            pagination: {
                el: positionContainer,
                type: 'fraction',
            },
        });

        swiper.on('init', () => {
            updateMeta();
            updateIndex();
        });

        swiper.on('activeIndexChange', () => {
            updateIndex();
        });

        swiper.on('slideChangeTransitionEnd', () => {
            updateMeta();
            updateIndex();
        });

        swiper.on('slideChange', updateNavigationButtons);

        swiper.init();

        if (fullscreenButton) {
            fullscreenButton.addEventListener('click', () => {
                if (isGalleryFullscreen()) {
                    endFullscreen();
                } else {
                    startFullscreen();
                }
            }, false);
        }

        document.addEventListener('keyup', event => {
            if (event.defaultPrevented) {
                return;
            }

            if (isGalleryFullscreen() === false) {
                return;
            }

            switch (event.key || event.code) {
                case 'Escape':
                case 'Esc':
                    endFullscreen();
                    break;
                case 'ArrowLeft':
                case 'Left':
                    swiper.slidePrev();
                    break;
                case 'ArrowRight':
                case 'Right':
                    swiper.slideNext();
                    break;
                default:
                    return;
            }
        });
    });
}
